import { Link } from "react-router-dom";
import { AD_FORMATS, adFormatOf, useSponsors, type AdFormat, type Sponsor } from "@/hooks/useSponsors";

// Cada espacio respeta la proporción real del arte, así lo que se ve en una
// captura es exactamente el tamaño que se le pide al anunciante.
const ratioClass: Record<AdFormat, string> = {
  cintillo: "aspect-[728/90]",
  mediano: "aspect-[300/250]",
  pequeno: "aspect-[300/125]",
};

const sizeText = (format: AdFormat) => `${AD_FORMATS[format].width} × ${AD_FORMATS[format].height} px`;

export interface AdPlaceholder {
  title: string;
  desc: string;
  format?: Exclude<AdFormat, "cintillo">;
  /** Franja de color inferior, como en las tarjetas de Turismo. */
  accentClass?: string;
}

/** Cintillo horizontal (728×90) que va justo debajo del encabezado de cada sección. */
export function AdCintillo({ placement, className = "bg-white" }: { placement: string; className?: string }) {
  const { sponsors, loading } = useSponsors(placement, "cintillo");
  const sponsor = sponsors[0];

  return (
    <section className={`py-4 md:py-5 ${className}`}>
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="max-w-[728px] mx-auto">
          <p
            className="text-slate-400 text-[9px] font-bold tracking-widest uppercase text-center mb-1.5"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Publicidad
          </p>
          {loading ? (
            <div className={`${ratioClass.cintillo} bg-slate-100 rounded-lg animate-pulse`} />
          ) : sponsor ? (
            <a
              href={sponsor.website_url || "#"}
              target="_blank"
              rel="nofollow noreferrer"
              className={`block ${ratioClass.cintillo} rounded-lg overflow-hidden border border-slate-100`}
            >
              {sponsor.banner_url ? (
                <img src={sponsor.banner_url} alt={sponsor.company_name} className="w-full h-full object-cover object-center" />
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-[#0B1F3A] to-[#1a3a6b] flex items-center justify-center">
                  <p className="text-white font-bold text-sm">{sponsor.company_name}</p>
                </div>
              )}
            </a>
          ) : (
            <Link
              to="/publicidad"
              className="group flex items-center gap-3 min-h-[64px] sm:min-h-0 sm:aspect-[728/90] px-4 rounded-lg border border-dashed border-slate-300 bg-white hover:border-red-300 hover:bg-red-50/30 transition-all"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-slate-100 rounded-lg flex-shrink-0 group-hover:bg-red-100 transition-colors">
                <i className="ri-megaphone-line text-slate-400 group-hover:text-red-500 transition-colors" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-500 font-semibold text-xs" style={{ fontFamily: "'Sora', sans-serif" }}>
                  Espacio publicitario disponible
                </p>
                <p className="text-slate-400 text-[10px] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Cintillo · {sizeText("cintillo")}
                </p>
              </div>
              <span className="ml-auto inline-flex items-center gap-1 text-red-500 text-[11px] font-semibold whitespace-nowrap">
                Anunciar aquí <i className="ri-arrow-right-line" />
              </span>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

function SponsorCard({ sponsor }: { sponsor: Sponsor }) {
  return (
    <a
      href={sponsor.website_url || "#"}
      target="_blank"
      rel="nofollow noreferrer"
      className="relative rounded-xl overflow-hidden border border-slate-100 group cursor-pointer hover:-translate-y-0.5 transition-transform block"
    >
      <div
        className="absolute top-2 left-2 z-10 bg-black/50 text-white text-[9px] font-bold px-2 py-0.5 rounded-full tracking-widest uppercase"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        Publicidad
      </div>
      <div className={`${ratioClass[adFormatOf(sponsor.ad_type)]} overflow-hidden`}>
        {sponsor.banner_url ? (
          <img
            src={sponsor.banner_url}
            alt={sponsor.company_name}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-[#0B1F3A] to-[#1a3a6b] flex items-center justify-center">
            <p className="text-white font-bold text-sm">{sponsor.company_name}</p>
          </div>
        )}
      </div>
      <div className="p-3 bg-gradient-to-r from-[#0B1F3A] to-[#1a3a6b]">
        <p className="text-white font-bold text-sm" style={{ fontFamily: "'Sora', sans-serif" }}>
          {sponsor.company_name}
        </p>
        {sponsor.notes && (
          <p className="text-blue-200/70 text-xs mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {sponsor.notes}
          </p>
        )}
      </div>
    </a>
  );
}

function EmptyAdSlot({ title, desc, format = "pequeno", accentClass }: AdPlaceholder) {
  return (
    <Link
      to="/publicidad"
      className="relative rounded-xl overflow-hidden border border-dashed border-slate-200 group cursor-pointer hover:border-red-300 hover:bg-red-50/30 transition-all block"
    >
      <span className="absolute top-2 right-2 text-slate-400 text-[9px] font-semibold bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">
        {sizeText(format)}
      </span>
      <div className={`${ratioClass[format]} flex flex-col items-center justify-center text-center p-4`}>
        <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-lg mb-2 group-hover:bg-red-100 transition-colors">
          <i className="ri-megaphone-line text-slate-400 group-hover:text-red-500 transition-colors" />
        </div>
        <p className="text-slate-500 font-semibold text-xs" style={{ fontFamily: "'Sora', sans-serif" }}>
          {title}
        </p>
        <p className="text-slate-400 text-[10px] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {desc}
        </p>
        <span className="mt-2 inline-flex items-center gap-1 text-red-500 text-[10px] font-semibold">
          Anunciar aquí <i className="ri-arrow-right-line" />
        </span>
      </div>
      {accentClass && (
        <div className={`p-3 bg-gradient-to-r ${accentClass}`}>
          <div className="flex items-center justify-between">
            <span className="text-white/80 text-[10px] font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Espacio publicitario disponible
            </span>
            <i className="ri-arrow-right-line text-white/60 text-xs" />
          </div>
        </div>
      )}
    </Link>
  );
}

/**
 * Columna derecha de publicidad. Muestra los banners activos de la sección
 * (pequeño 300×125 o mediano 300×250) y, si no hay, los espacios disponibles.
 */
export function AdSidebar({ placement, placeholders, max = 2 }: { placement: string; placeholders: AdPlaceholder[]; max?: number }) {
  const { sponsors, loading } = useSponsors(placement);

  return (
    <div className="flex flex-col gap-4">
      {loading
        ? placeholders.map((p) => (
            <div key={p.title} className={`${ratioClass[p.format ?? "pequeno"]} bg-slate-100 rounded-xl animate-pulse`} />
          ))
        : sponsors.length > 0
          ? sponsors.slice(0, max).map((sponsor) => <SponsorCard key={sponsor.id} sponsor={sponsor} />)
          : placeholders.map((p) => <EmptyAdSlot key={p.title} {...p} />)}
    </div>
  );
}
